import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { AlertController, IonInput, ModalController } from "@ionic/angular";
import { ApiService, AssetHierarchyResponse } from "source/app/configuration/services/api.service";
import {
	AssignmentDefinition,
	AssignmentDefinitionState,
	AssignmentSolutionData,
	AssignmentSourceContext,
	ManifestAssignment,
	MobaroAsset,
} from "source/app/features/assignments/assignment.interface";

import { FormControl, FormGroup } from "@angular/forms";
import { AssignmentTrigger, ImageAttachment } from "../../checklists/checklist.interface";
import { AssignmentFormService } from "../services/assignment.form.service";
import { RideOpsUiService } from "source/app/configuration/services/rideops-ui.service";
import { TranslocoService } from "@ngneat/transloco";

@Component({
	selector: "app-assignment",
	templateUrl: "./assignment-modal.component.html",
	styleUrls: ["assignment-modal.component.scss"],
})
export class AssignmentModalComponent implements OnInit {
	static modalId = "assignment-modal-component";
	constructor(
		private modalController: ModalController,
		private alertController: AlertController,
		private apiService: ApiService,
		private assignmentFormService: AssignmentFormService,
		private rideOpsUi: RideOpsUiService,
		private transloco: TranslocoService,
	) {}
	@ViewChild("title") title!: IonInput;

	@Input() assignment: ManifestAssignment;
	@Input() definition: AssignmentDefinition;
	@Input() solution: AssignmentSolutionData;
	@Input() trigger: AssignmentTrigger;
	@Input() sourceContext: AssignmentSourceContext;

	assignmentForm: FormGroup<AssignmentForm>;
	ready = false;
	categories: string[] = [];
	assets: AssetHierarchyResponse;
	states: AssignmentDefinitionState[] = [];
	viewConfig: AssignmentViewConfig;
	moreDetailsOpen = false;
	solutionOpen = false;

	modules = {
		toolbar: [
			["bold", "italic", "underline", "strike"],
			[{ list: "ordered" }, { list: "bullet" }],
		],
	};

	modulesV2 = {
		toolbar: [["bold", "italic"], [{ list: "bullet" }]],
	};

	isV2 = (): boolean => this.rideOpsUi.isV2Active();

	isCreate = (): boolean => this.assignment?.id == null;

	moreDetailsHint = (): string => {
		const parts: string[] = [];
		if (this.definition && this.assignmentForm?.controls.solution.value == null && this.states.length > 0) {
			parts.push(this.transloco.translate("ASSIGNMENT__STATE"));
		}
		if (this.assets?.nodes?.length > 0) {
			parts.push(this.transloco.translate("ASSIGNMENT__ASSET"));
		}
		if (this.viewConfig?.showCategories && this.categories.length > 0) {
			parts.push(this.transloco.translate("ASSIGNMENT__CATEGORIES"));
		}
		if (this.definition?.categories?.length > 0 && this.viewConfig?.showDefinitionCategories) {
			parts.push(this.transloco.translate("ASSIGNMENT__DEFINITION_CATEGORIES"));
		}
		return parts.join(" · ");
	};

	close = () => {
		this.modalController.dismiss();
	};

	setPriority = (high: boolean) => {
		this.assignmentForm.controls.priority.setValue(high);
	};

	categoriesSummary = (): string => {
		const selected = this.assignmentForm?.controls.categories.value || [];
		return selected.length ? selected.join(", ") : "";
	};

	definitionCategoriesSummary = (): string => {
		const selected = this.assignmentForm?.controls.definitionCategories.value || [];
		if (!selected.length || !this.definition?.categories) {
			return "";
		}
		return this.definition.categories
			.filter((category) => selected.includes(category.id))
			.map((category) => category.name)
			.join(", ");
	};

	openCategoriesPicker = async () => {
		const selected = new Set(this.assignmentForm.controls.categories.value || []);
		const alert = await this.alertController.create({
			header: this.transloco.translate("ASSIGNMENT__CATEGORIES"),
			cssClass: "assignment-select-alert-v2",
			inputs: this.categories.map((category) => ({
				type: "checkbox" as const,
				label: category,
				value: category,
				checked: selected.has(category),
			})),
			buttons: [
				{
					text: this.transloco.translate("ASSIGNMENT__BUTTON_CANCEL"),
					role: "cancel",
				},
				{
					text: this.transloco.translate("ASSIGNMENT__BUTTON_SAVE"),
					role: "confirm",
					handler: (values: string[]) => {
						this.assignmentForm.controls.categories.setValue(values || []);
					},
				},
			],
		});
		await alert.present();
	};

	openDefinitionCategoriesPicker = async () => {
		const multiple = this.definition?.categorySelectionType === "Multiple";
		const selected = this.assignmentForm.controls.definitionCategories.value || [];
		const selectedSet = new Set(selected);
		const alert = await this.alertController.create({
			header: this.transloco.translate("ASSIGNMENT__DEFINITION_CATEGORIES"),
			cssClass: "assignment-select-alert-v2",
			inputs: (this.definition?.categories || []).map((category) => ({
				type: (multiple ? "checkbox" : "radio") as "checkbox" | "radio",
				label: category.name,
				value: category.id,
				checked: selectedSet.has(category.id),
			})),
			buttons: [
				{
					text: this.transloco.translate("ASSIGNMENT__BUTTON_CANCEL"),
					role: "cancel",
				},
				{
					text: this.transloco.translate("ASSIGNMENT__BUTTON_SAVE"),
					role: "confirm",
					handler: (value: string | string[]) => {
						if (multiple) {
							this.assignmentForm.controls.definitionCategories.setValue((value as string[]) || []);
						} else {
							this.assignmentForm.controls.definitionCategories.setValue(value ? [value as string] : []);
						}
					},
				},
			],
		});
		await alert.present();
	};

	save = () => {
		if (this.assignmentForm.invalid) {
			this.assignmentForm.markAllAsTouched();
			return;
		}

		const assignmentWithData = this.assignmentFormService.AssignmentFromForm(
			this.assignment,
			this.assignmentForm,
			this.trigger,
		);
		this.modalController.dismiss(assignmentWithData, "save", AssignmentModalComponent.modalId);
	};

	createAssignmentViewConfig = async (
		trigger: AssignmentTrigger,
		definition: AssignmentDefinition,
	): Promise<AssignmentViewConfig> => {
		let showResolve = true;
		const shouldEvaluateShowResolve = definition?.limitResolversTo?.length > 0;
		if (shouldEvaluateShowResolve) {
			const userMemberships = await this.apiService.memberships();
			showResolve = userMemberships.some((membership) => definition.limitResolversTo.includes(membership));
		}

		if (trigger === null) {
			return {
				showPriority: true,
				showDateTime: true,
				showAssignee: true,
				showCategories: true,
				showDefinitionCategories: true,
				showResolve,
			};
		}
		return {
			showPriority: trigger.priority === false,
			showDateTime: trigger.deadline === false,
			showAssignee: trigger.assignees.length === 0,
			showCategories: trigger.categories.length === 0,
			showDefinitionCategories: trigger.definitionCategories == null || trigger.definitionCategories.length === 0,
			showResolve,
		};
	};

	compareWith = (o1: { id: string }, o2: { id: string }) => {
		return o1 && o2 ? o1.id === o2.id : o1 === o2;
	};

	async ngOnInit(): Promise<void> {
		if (this.definition != null) {
			this.states = this.definition.states.filter((x) => x.type !== "Final");
		}

		[this.assets, this.categories] = await Promise.all([
			this.apiService.assets(),
			this.apiService.assignmentCategories(),
		]);

		this.viewConfig = await this.createAssignmentViewConfig(this.trigger, this.definition);
		this.assignmentForm = this.assignmentFormService.createAssignmentForm(this.assignment, this.solution);
		this.ready = true;

		setTimeout(() => {
			if (this.title.value == null || this.title.value == "") {
				this.title.setFocus();
			}
		}, 500);
	}
}

export interface AssignmentForm {
	title: FormControl<string>;
	attachments: FormControl<ImageAttachment[]>;
	description: FormControl<string>;
	definitionState: FormControl<DocumentReference>;
	definitionPriority: FormControl<DocumentReference>;
	priority: FormControl<boolean>;
	asset: FormControl<MobaroAsset>;
	assignees: FormControl<DocumentReference[]>;
	categories: FormControl<string[]>;
	definitionCategories: FormControl<string[]>;
	start: FormControl<string>;
	end: FormControl<string>;
	solution: FormControl<AssignmentSolutionData>;
}

export interface AssignmentViewConfig {
	showPriority: boolean;
	showDateTime: boolean;
	showAssignee: boolean;
	showCategories: boolean;
	showDefinitionCategories: boolean;
	showResolve: boolean;
}
